import Alpine from "alpinejs";

export const initSmoothAnchorScroll = () => {
  const scrollToTarget = (target: HTMLElement, behavior: ScrollBehavior) => {
    target.scrollIntoView({
      block: "start",
      behavior,
    });
  };

  const handleInitialHashScroll = () => {
    if (!window.location.hash) return;

    const decodedHash = decodeURIComponent(window.location.hash);
    const target = document.querySelector<HTMLElement>(decodedHash);
    if (!(target instanceof HTMLElement)) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    // Prevent browser default hash jump (which can occur after DOMContentLoaded)
    // by temporarily removing the hash from the URL. We'll restore it after scrolling.
    const originalHash = window.location.hash;
    if (originalHash) {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const initialScrollStartY = window.scrollY;
        scrollToTarget(target, prefersReducedMotion ? "auto" : "smooth");

        let verificationFrame = 0;
        const verifyAndFallbackInitialScroll = () => {
          verificationFrame += 1;
          if (verificationFrame < 20) {
            requestAnimationFrame(verifyAndFallbackInitialScroll);
            return;
          }

          const currentScrollY = window.scrollY;
          const movedDistance = Math.abs(currentScrollY - initialScrollStartY);
          const remainingDistance = Math.abs(target.getBoundingClientRect().top);
          const needsFallback =
            movedDistance < 10 && remainingDistance > 20;

          if (needsFallback) {
            window.scrollTo({
              top: currentScrollY + target.getBoundingClientRect().top,
              left: 0,
              behavior: "auto",
            });
          }

          if (originalHash) {
            window.history.replaceState(null, "", originalHash);
          }
        };

        requestAnimationFrame(verifyAndFallbackInitialScroll);
      });
    });
  };

  const handleSmoothAnchorClick = (event: MouseEvent) => {
    const link = (event.target as HTMLElement | null)?.closest("a[href*='#']");
    if (!(link instanceof HTMLAnchorElement)) return;
    if (!link.hash || link.hash === "#") return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target && link.target !== "_self") return;

    const destination = new URL(link.href, window.location.href);
    if (destination.origin !== window.location.origin) return;
    const decodedHash = decodeURIComponent(destination.hash);

    // If the target exists on the current page, prefer in-page smooth scroll
    // even when the link points to another pathname (e.g. "/#cta").
    const localTarget = document.querySelector<HTMLElement>(decodedHash);
    if (
      localTarget instanceof HTMLElement &&
      destination.pathname !== window.location.pathname
    ) {
      event.preventDefault();

      const menuStore = Alpine.store("menu") as
        | { isOpen?: boolean; closeMenu?: () => void }
        | undefined;
      if (menuStore?.isOpen && typeof menuStore.closeMenu === "function") {
        menuStore.closeMenu();
      }

      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      requestAnimationFrame(() => {
        scrollToTarget(localTarget, prefersReducedMotion ? "auto" : "smooth");
      });

      if (window.location.hash !== destination.hash) {
        window.history.pushState(null, "", destination.hash);
      }
      return;
    }

    const isSamePath = destination.pathname === window.location.pathname;
    if (!isSamePath) return;

    const target = localTarget ?? document.querySelector<HTMLElement>(decodedHash);
    if (!(target instanceof HTMLElement)) return;

    event.preventDefault();

    const menuStore = Alpine.store("menu") as { isOpen?: boolean; closeMenu?: () => void } | undefined;
    if (menuStore?.isOpen && typeof menuStore.closeMenu === "function") {
      menuStore.closeMenu();
    }

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    requestAnimationFrame(() => {
      scrollToTarget(target, prefersReducedMotion ? "auto" : "smooth");
    });

    if (window.location.hash !== destination.hash) {
      window.history.pushState(null, "", destination.hash);
    }
  };

  document.addEventListener("click", handleSmoothAnchorClick);
  handleInitialHashScroll();

  return () => {
    document.removeEventListener("click", handleSmoothAnchorClick);
  };
};
